import { useMemo } from 'react';
import clsx from 'clsx';

export type MovementState =
  | 'NEW_DONOR'
  | 'BRIDGE'
  | 'RECEIVER'
  | 'PAYOUT'
  | 'REINVEST'
  | 'EXITED';

interface TimelineStep {
  state: MovementState;
  label: string;
  icon: string;
  description: string;
}

const steps: TimelineStep[] = [
  {
    state: 'NEW_DONOR',
    label: 'Nuovo Donatore',
    icon: '�️',
    description: 'Hai donato e sei entrato in un City-Box.',
  },
  {
    state: 'BRIDGE',
    label: 'Bridge',
    icon: '🌉',
    description: 'Accompagni i nuovi donatori nel tuo box.',
  },
  {
    state: 'RECEIVER',
    label: 'Receiver',
    icon: '💎',
    description: 'Sei il ricevente del tuo City-Box.',
  },
  {
    state: 'PAYOUT',
    label: 'Payout',
    icon: '💰',
    description: 'Il payout è stato erogato.',
  },
  {
    state: 'REINVEST',
    label: 'Reinvest',
    icon: '♻️',
    description: 'Hai reinvestito nel ciclo del dono.',
  },
];

interface MovementTimelineProps {
  currentState: MovementState;
  /** Visualizzazione compatta per card/dashboard */
  compact?: boolean;
  className?: string;
}

export default function MovementTimeline({
  currentState,
  compact = false,
  className,
}: MovementTimelineProps) {
  const currentIndex = useMemo(() => {
    if (currentState === 'EXITED') return steps.length; // tutti completati
    return steps.findIndex((s) => s.state === currentState);
  }, [currentState]);

  if (currentState === 'EXITED') {
    return (
      <div className={clsx('card-flat text-center py-4', className)}>
        <span className="text-2xl mb-1 block">🚪</span>
        <p className="text-sm font-medium text-heading">Uscito dal Movimento</p>
        <p className="text-xs text-muted">Hai completato il tuo ciclo e scelto di uscire.</p>
      </div>
    );
  }

  if (compact) {
    return (
      <div className={clsx('flex items-center gap-1', className)}>
        {steps.map((step, i) => {
          const isActive = i === currentIndex;
          const isPast = i < currentIndex;
          return (
            <div key={step.state} className="flex items-center">
              <div
                className={clsx(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm transition-all',
                  isActive && 'bg-primary-500 text-white ring-2 ring-primary-500/30 dark:ring-primary-500/20 scale-110',
                  isPast && 'bg-primary-100 text-primary-600 dark:bg-primary-500/20 dark:text-primary-400',
                  !isActive && !isPast && 'bg-surface-100 text-surface-400 dark:bg-surface-800 dark:text-surface-500'
                )}
                title={step.label}
              >
                {isPast ? '✓' : step.icon}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={clsx(
                    'w-4 h-0.5 transition-colors',
                    i < currentIndex
                      ? 'bg-primary-400 dark:bg-primary-500'
                      : 'bg-surface-200 dark:bg-surface-700'
                  )}
                />
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Full version
  return (
    <div className={clsx('space-y-0', className)}>
      {steps.map((step, i) => {
        const isActive = i === currentIndex;
        const isPast = i < currentIndex;
        const isLast = i === steps.length - 1;

        return (
          <div key={step.state} className="flex gap-4">
            {/* Vertical line + circle */}
            <div className="flex flex-col items-center">
              <div
                className={clsx(
                  'w-10 h-10 rounded-full flex items-center justify-center text-lg transition-all flex-shrink-0',
                  isActive && 'bg-primary-500 text-white ring-4 ring-primary-500/20 dark:ring-primary-500/10 shadow-lg shadow-primary-500/20',
                  isPast && 'bg-primary-100 text-primary-600 dark:bg-primary-500/20 dark:text-primary-400',
                  !isActive && !isPast && 'bg-surface-100 text-surface-400 dark:bg-surface-800 dark:text-surface-500 border-2 border-dashed border-surface-300 dark:border-surface-600'
                )}
              >
                {isPast ? '✓' : step.icon}
              </div>
              {!isLast && (
                <div
                  className={clsx(
                    'w-0.5 flex-1 min-h-[2rem] transition-colors',
                    i < currentIndex
                      ? 'bg-primary-400 dark:bg-primary-500'
                      : 'bg-surface-200 dark:bg-surface-700'
                  )}
                />
              )}
            </div>

            {/* Content */}
            <div className={clsx('pb-6', isLast && 'pb-0')}>
              <div className="flex items-center gap-2 mb-0.5">
                <h4
                  className={clsx(
                    'font-semibold text-sm',
                    isActive && 'text-primary-600 dark:text-primary-400',
                    isPast && 'text-heading',
                    !isActive && !isPast && 'text-muted'
                  )}
                >
                  {step.label}
                </h4>
                {isActive && (
                  <span className="badge-green text-[10px] animate-pulse">Attuale</span>
                )}
                {isPast && (
                  <span className="badge-gray text-[10px]">Completato</span>
                )}
              </div>
              <p
                className={clsx(
                  'text-xs',
                  isActive || isPast ? 'text-muted' : 'text-surface-400 dark:text-surface-600'
                )}
              >
                {step.description}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
